/**
 * Centralized logging utility for the application.
 * Provides structured logging with environment-aware behavior.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: string;
  data?: unknown;
  error?: Error;
}

interface ErrorTrackingPayload {
  message: string;
  stack?: string;
  context?: string;
  metadata?: Record<string, unknown>;
  url: string;
  userAgent: string;
  timestamp: string;
}

// Queue for batching error reports
const errorQueue: ErrorTrackingPayload[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;

const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

// Error tracking endpoint (configure via env)
const ERROR_TRACKING_ENDPOINT = import.meta.env.VITE_ERROR_TRACKING_ENDPOINT || '';

/**
 * Flush error queue to tracking service
 */
async function flushErrorQueue(): Promise<void> {
  if (errorQueue.length === 0 || !ERROR_TRACKING_ENDPOINT) return;

  const errors = [...errorQueue];
  errorQueue.length = 0;

  try {
    await fetch(ERROR_TRACKING_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ errors }),
    });
  } catch {
    // Silently fail - don't want error tracking to cause errors
    if (isDevelopment) {
      console.warn('[Logger] Failed to send errors to tracking service');
    }
  }
}

/**
 * Schedule error queue flush
 */
function scheduleFlush(): void {
  if (flushTimeout) return;
  flushTimeout = setTimeout(() => {
    flushTimeout = null;
    flushErrorQueue();
  }, 5000); // Batch errors every 5 seconds
}

/**
 * Format log entry for console output
 */
function formatLogEntry(entry: LogEntry): string {
  const prefix = `[${entry.timestamp}] [${entry.level.toUpperCase()}]`;
  const context = entry.context ? ` [${entry.context}]` : '';
  return `${prefix}${context} ${entry.message}`;
}

/**
 * Create a log entry
 */
function createLogEntry(
  level: LogLevel,
  message: string,
  context?: string,
  data?: unknown,
  error?: Error
): LogEntry {
  return {
    level,
    message,
    timestamp: new Date().toISOString(),
    context,
    data,
    error,
  };
}

/**
 * Logger instance with methods for each log level
 */
export const logger = {
  /**
   * Simple log - matches console.log signature (dev only)
   * Use this for quick debugging, it's completely removed in production
   */
  log(...args: unknown[]): void {
    if (!isDevelopment) return;
    console.log(...args);
  },

  /**
   * Debug level - only shown in development
   */
  debug(message: string, context?: string, data?: unknown): void {
    if (!isDevelopment) return;
    const entry = createLogEntry('debug', message, context, data);
    console.log(formatLogEntry(entry), data ?? '');
  },

  /**
   * Info level - general information
   */
  info(message: string, context?: string, data?: unknown): void {
    if (isProduction) return; // Suppress info logs in production
    const entry = createLogEntry('info', message, context, data);
    console.info(formatLogEntry(entry), data ?? '');
  },

  /**
   * Simple warn - matches console.warn signature (dev only)
   */
  simpleWarn(...args: unknown[]): void {
    if (!isDevelopment) return;
    console.warn(...args);
  },

  /**
   * Warning level - potential issues
   */
  warn(message: string, context?: string, data?: unknown): void {
    const entry = createLogEntry('warn', message, context, data);
    if (isDevelopment) {
      console.warn(formatLogEntry(entry), data ?? '');
    }
  },

  /**
   * Simple error - matches console.error signature (dev only for verbose errors)
   */
  simpleError(...args: unknown[]): void {
    if (!isDevelopment) return;
    console.error(...args);
  },

  /**
   * Error level - errors that need attention
   */
  error(message: string, error?: Error, context?: string, metadata?: Record<string, unknown>): void {
    const entry = createLogEntry('error', message, context, metadata, error);

    // Always log to console in development
    if (isDevelopment) {
      console.error(formatLogEntry(entry), error ?? '', metadata ?? '');
    }

    // Track error for production monitoring
    if (isProduction || ERROR_TRACKING_ENDPOINT) {
      const payload: ErrorTrackingPayload = {
        message,
        stack: error?.stack,
        context,
        metadata,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: entry.timestamp,
      };
      errorQueue.push(payload);
      scheduleFlush();
    }
  },

  /**
   * Track a custom event (for analytics)
   */
  track(eventName: string, properties?: Record<string, unknown>): void {
    if (isDevelopment) {
      console.log(`[TRACK] ${eventName}`, properties ?? '');
    }
    // In production, send to analytics service
    // analytics.track(eventName, properties);
  },

  /**
   * Create a child logger with preset context
   */
  withContext(context: string) {
    return {
      debug: (message: string, data?: unknown) => logger.debug(message, context, data),
      info: (message: string, data?: unknown) => logger.info(message, context, data),
      warn: (message: string, data?: unknown) => logger.warn(message, context, data),
      error: (message: string, error?: Error, metadata?: Record<string, unknown>) =>
        logger.error(message, error, context, metadata),
      track: (eventName: string, properties?: Record<string, unknown>) =>
        logger.track(`${context}:${eventName}`, properties),
    };
  },
};

// Flush any pending errors before page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (flushTimeout) {
      clearTimeout(flushTimeout);
    }
    flushErrorQueue();
  });

  // Global error handler
  window.addEventListener('error', (event) => {
    logger.error('Uncaught error', event.error, 'global', {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  // Unhandled promise rejection handler — show toast for user visibility
  window.addEventListener('unhandledrejection', (event) => {
    logger.error(
      'Unhandled promise rejection',
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      'global'
    );
    // Show user-facing toast for async errors
    import('@/contexts/ToastContext').then(({ showGlobalErrorToast }) => {
      const msg = event.reason instanceof Error ? event.reason.message : String(event.reason);
      showGlobalErrorToast('Something went wrong', msg.slice(0, 200));
    }).catch(() => {});
  });
}

export default logger;
